<template>
	<div v-if="display.smAndDown.value" class="overlay-container">
		<v-overlay v-model="dialog" :z-index="10010" class="upgrade-overlay-mobile">
			<div class="d-flex align-center justify-center text-center h-100 w-100 pa-4">
				<h5 v-if="subscription && isNewSubscription" class="text-black">
					<a @click.prevent="upgrade()">Upgrade</a>
					your subscription to access all features.
				</h5>
				<h5 v-else class="text-black">
					Your trial has expired. Please contact <a :href="`mailto:${helpEmail}`">{{ helpEmail }}</a> to set up your account.
				</h5>
			</div>
		</v-overlay>
		<v-btn v-show="dialog" icon="icon-close-large" variant="plain" class="close-button" color="black" @click="close"> </v-btn>
	</div>
	<div v-else>
		<Dialog :dialog-visible="dialog" :is-angry="true" :remove-cancel-button="true" @cancel="close" @agree="close">
			<template #dialog-header="{ titleId }">
				<div v-if="isTrialCCNow" class="d-flex align-center w-100">
					<h4 :id="titleId">Start Your Free Trial</h4>
					<v-spacer />
					<v-btn icon variant="plain" aria-label="Close" @click="close">
						<v-icon>icon-close-medium</v-icon>
					</v-btn>
				</div>
				<h4 v-else :id="titleId">{{ upgradeDialogTitle }}</h4>
			</template>
			<template #dialog-body>
				<div v-if="isTrialCCNow">
					<div v-if="subscription && isNewSubscription">
						<p v-if="canUpdateOrg" class="upgrade-message">
							This is a premium feature. <a @click.prevent="upgrade()">Select your plan</a> to start your free trial and get
							access to this feature.
						</p>
						<p v-else class="upgrade-message">
							This is a premium feature. Contact an administrator to select your plan to start your free trial and get access
							to this feature.
						</p>
					</div>
					<div v-else>
						<p class="upgrade-message">
							This is a premium feature. Contact <a :href="`mailto:${helpEmail}`">{{ helpEmail }}</a> to select your plan to
							start your free trial and get access to this feature.
						</p>
					</div>
				</div>
				<div v-else>
					<div v-if="subscription && isNewSubscription">
						<p v-if="!canUpdateOrg" class="upgrade-message">
							This feature is available with a paid plan. Please ask an administrator to update your account.
						</p>
						<p v-else-if="trialExpired && !subscription.hasPaymentMethod" class="upgrade-message">
							Your free trial has ended.
							<a @click.prevent="upgrade()" class="text-primary cursor-pointer">Select a plan</a>
							to keep using this feature.
						</p>
						<p v-else-if="trialExpired" class="upgrade-message">
							Your free trial has ended. Please
							<a @click.prevent="upgrade()">review your billing details</a>
							to keep using this feature.
						</p>
						<p v-else-if="trialDaysRemaining > 0 && !subscription.hasPaymentMethod" class="upgrade-message">
							{{ trialDays }} remain on your free trial.
							<a @click.prevent="upgrade()" class="text-primary cursor-pointer">Select a plan</a>
							before your trial ends to keep using this feature.
						</p>
						<p v-else-if="trialDaysRemaining > 0 && subscription.hasPaymentMethod" class="upgrade-message">
							{{ trialDays }} remain on your free trial. At the end of your trial, we will charge the card on file
							automatically.
						</p>
						<p v-else class="upgrade-message">
							This is a premium feature.
							<a @click.prevent="upgrade()" class="text-primary cursor-pointer">Select your plan</a>
							to continue using premium features.
						</p>
					</div>
					<div v-else>
						<p class="upgrade-message">
							Please contact <a :href="`mailto:${helpEmail}`">{{ helpEmail }}</a> to set up your account.
						</p>
					</div>
				</div>
			</template>
		</Dialog>
	</div>
</template>

<script>
	import Account from "../api/account";
	import api from "@/api";
	import constants from "@/api/constants";
	import Dialog from "./Dialog.vue";
	import { useDisplay } from "vuetify";
	import { isNewSubscription, effectiveTrialDaysRemaining } from "../helpers/subscription";

	export default {
		components: {
			Dialog
		},
		setup() {
			const display = useDisplay();
			return { display };
		},
		data() {
			return {
				dialog: false,
				organizationId: null,
				canUpdateOrg: false,
				isTrialCCNow: false,
				subscription: null,
				userEmail: null,
				trialDaysRemaining: null,
				helpEmail: window.env.VITE_SUPPORT_EMAIL || "help@liveswitch.com",
				isNewSubscription: false
			};
		},
		computed: {
			subscriptionUrl() {
				if (this.organizationId) {
					return `/subscriptions/${this.organizationId}`;
				} else {
					return "";
				}
			},
			trialDays() {
				if (this.trialDaysRemaining != 1) {
					let days = this.trialDaysRemaining;

					if (days < 0) {
						days = 0;
					}

					return `${days} days`;
				} else {
					return "1 day";
				}
			},
			trialExpired() {
				return this.subscription?.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL_EXPIRED;
			},
			upgradeDialogTitle() {
				const newSubscription = Boolean(this.subscription && this.isNewSubscription);
				const hasPaymentMethod = Boolean(this.subscription?.hasPaymentMethod);

				if (newSubscription && !this.canUpdateOrg) return "Plan Update Needed";
				if (newSubscription && this.trialExpired && !hasPaymentMethod) return "Your Free Trial Has Ended";
				if (newSubscription && this.trialExpired && hasPaymentMethod) return "Account Attention Needed";
				if (newSubscription && this.trialDaysRemaining > 0) return "Your Free Trial Is Active";
				if (!newSubscription) return "Your Trial Has Expired";
				return "Account Setup Needed";
			}
		},
		methods: {
			async open(organization) {
				const me = await api.getMe();
				const account = new Account(me.account);
				this.canUpdateOrg = account.hasPermissionTo("UPDATE_ORGANIZATION");
				this.organizationId = organization.id;
				this.isTrialCCNow = organization.isTrialCCNow;

				// Load subscription info if available. Use the subscription's own trial countdown for new
				// subscriptions — the org-level value can be stale for trial-extended orgs (LSP-15991).
				const subscription = me.organizationSubscription;
				if (subscription) {
					const trialDaysRemaining = effectiveTrialDaysRemaining(subscription, me.organization.trialDaysRemaining);
					this.setSubscriptionInfo(subscription, me.user.email, trialDaysRemaining);
				}

				// Open after data is loaded so upgradeDialogTitle renders with the correct isNewSubscription
				// value from the first paint — avoids a flash of "Your Trial Has Expired" for new-sub orgs.
				this.dialog = true;

				return new Promise((resolve, reject) => {
					this.resolve = resolve;
					this.reject = reject;
				});
			},
			setSubscriptionInfo(subscription, userEmail, trialDaysRemaining) {
				this.subscription = subscription;
				this.userEmail = userEmail;
				this.trialDaysRemaining = trialDaysRemaining;
				this.isNewSubscription = isNewSubscription(subscription);
			},
			close() {
				if (this.resolve) {
					this.resolve(false);
				}
				this.dialog = false;
			},
			upgrade() {
				this.close();
				this.$emit("upgrade");
			}
		}
	};
</script>
<style scoped>
	.upgrade-overlay-mobile {
		background-color: white;
	}

	.upgrade-overlay-mobile::v-deep > .v-overlay__scrim {
		background-color: white !important;
	}

	.upgrade-overlay-mobile :deep(.v-overlay__content) {
		height: 100%;
	}

	.close-button {
		position: fixed !important;
		top: 16px;
		right: 16px;
		z-index: 10011;
	}

	.text-black {
		color: black;
	}

	a {
		text-decoration: underline;
		color: rgb(var(--v-theme-primary));
	}

	.upgrade-message {
		word-break: break-word;
		margin-bottom: 0 !important;
	}
</style>
